import React, { useId } from 'react';
import { Box, Typography } from '@mui/material';

interface WordmarkProps {
  /** Height of the glyph mark in px. */
  size?: number;
  /** `dark` = placed on a dark surface (light text). */
  tone?: 'dark' | 'light';
  showText?: boolean;
  /** Render the mark with the brand gradient instead of a flat colour. */
  gradient?: boolean;
}

/**
 * Stratify mark + wordmark as inline SVG.
 *
 * Inline rather than the existing `logo192.png` because: it stays crisp at
 * 28px nav size (the raster is a 192px PWA icon), it recolours for the dark
 * hero and the light footer, and it costs no network request.
 *
 * The glyph is three ascending bars — strata, read literally: layers of
 * structure stacked and widening toward the base.
 */
const Wordmark: React.FC<WordmarkProps> = ({
  size = 32,
  tone = 'dark',
  showText = true,
  gradient = true,
}) => {
  // Unique per instance — duplicate SVG gradient ids across instances is a
  // classic silent rendering bug where every mark adopts the first one's fill.
  const rawId = useId();
  const gradientId = `stratify-mark-${rawId.replace(/:/g, '')}`;

  const flatColor = tone === 'dark' ? '#f4f6fb' : '#101828';
  const fill = gradient ? `url(#${gradientId})` : flatColor;

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.25 }}>
      <Box
        component="svg"
        viewBox="0 0 48 48"
        role="img"
        aria-label="Stratify"
        sx={{ width: size, height: size, display: 'block', flexShrink: 0 }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#6fa3ee" />
            <stop offset="100%" stopColor="#215fbe" />
          </linearGradient>
        </defs>

        {/* Rounded tile */}
        <rect x="0" y="0" width="48" height="48" rx="12" fill={fill} />

        {/* Three ascending strata bars, knocked out of the tile */}
        <rect x="14" y="14" width="20" height="5" rx="2.5" fill={tone === 'dark' ? '#070912' : '#ffffff'} />
        <rect x="11" y="21.5" width="26" height="5" rx="2.5" fill={tone === 'dark' ? '#070912' : '#ffffff'} opacity="0.82" />
        <rect x="8" y="29" width="32" height="5" rx="2.5" fill={tone === 'dark' ? '#070912' : '#ffffff'} opacity="0.64" />
      </Box>

      {showText && (
        <Typography
          component="span"
          sx={{
            fontSize: size * 0.56,
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: tone === 'dark' ? '#f4f6fb' : '#101828',
            lineHeight: 1,
            whiteSpace: 'nowrap',
          }}
        >
          Stratify
        </Typography>
      )}
    </Box>
  );
};

export default Wordmark;
